// Add or edit a sandbox host (docs/plans/2026-10-03-sandbox-hosts-ontwerp.md
// §11): a Docker daemon run sandboxes start on, reached over TCP (+ TLS) or
// SSH. The main server (`local`) only has a name, capacity and its switches.
//
// Secrets (TLS CA/certificate/key, the SSH private key) are write-only: the
// API only says whether one is stored, an empty field keeps it. The SSH host
// fingerprint is optional (§15.1 item 4): empty means the worker pins the key
// it sees on the first connection; clearing a recorded one makes it pin again.
//
// An existing host (the main server too) gets "Scan & advise" under the
// capacity fields (§16.6); "Apply advice" only fills the slot fields.

import { useState, type FormEvent, type ReactNode } from 'react'
import { useMutation } from '@tanstack/react-query'
import {
  CreateSandboxHostBodySchema,
  type CreateTunnelSandboxHostResponse,
  SANDBOX_HOST_EDITABLE_FIELDS,
  UpdateSandboxHostBodySchema,
  defaultSandboxHostPort,
  type CreateSandboxHostBody,
  type SandboxHost,
  type UpdateSandboxHostBody,
} from '@goblic/shared'
import { ApiError, sandboxHostsApi } from '../lib/api-client'
import { SandboxHostScanPanel } from './sandbox-host-scan-panel'
import { Field, Notice } from './ui'

type RemoteKind = 'tcp' | 'ssh'
type TlsSecret = 'tlsCa' | 'tlsCert' | 'tlsKey'

const KINDS: { value: RemoteKind; label: string; text: string }[] = [
  {
    value: 'tcp',
    label: 'Docker over TCP + TLS',
    text: 'The daemon listens on a port with client certificates, e.g. tcp://build-1:2376.',
  },
  {
    value: 'ssh',
    label: 'Docker over SSH',
    text: 'Goblic signs in with a key as a user in the docker group; the daemon stays on its socket.',
  },
]

const TLS_FIELDS: { key: TlsSecret; label: string; placeholder: string }[] = [
  { key: 'tlsCa', label: 'CA certificate', placeholder: '-----BEGIN CERTIFICATE-----' },
  { key: 'tlsCert', label: 'Client certificate', placeholder: '-----BEGIN CERTIFICATE-----' },
  { key: 'tlsKey', label: 'Client key', placeholder: '-----BEGIN PRIVATE KEY-----' },
]

const KEEP_HINT = 'Leave empty to keep the stored value.'

function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback
}

/** '' → null (no limit), else the number as typed (zod rejects what isn't a whole number). */
function slotsValue(text: string): number | null {
  return text.trim() === '' ? null : Number(text.trim())
}

function slotsText(value: number | null | undefined): string {
  return value == null ? '' : String(value)
}

export function SandboxHostForm({
  existing,
  autoScan = false,
  onDone,
}: {
  existing?: SandboxHost
  /** Start "Scan & advise" as soon as the form opens (the row action, or right after adding). */
  autoScan?: boolean
  onDone: (saved: SandboxHost) => void
}) {
  const [kind, setKind] = useState<RemoteKind>(
    existing && (existing.kind === 'tcp' || existing.kind === 'ssh') ? existing.kind : 'tcp'
  )
  const isLocal = existing?.kind === 'local'
  // Tunnel hosts have no address of their own: the agent dials in (installer ontwerp §1).
  const isTunnel = existing?.kind === 'tunnel'
  const [name, setName] = useState(existing?.name ?? '')
  const [host, setHost] = useState(existing?.host ?? '')
  const [port, setPort] = useState(existing?.port != null ? String(existing.port) : '')
  const [tlsEnabled, setTlsEnabled] = useState(existing?.tlsEnabled ?? true)
  const [tls, setTls] = useState<Record<TlsSecret, string>>({ tlsCa: '', tlsCert: '', tlsKey: '' })
  const [sshUser, setSshUser] = useState(existing?.sshUser ?? '')
  const [sshPrivateKey, setSshPrivateKey] = useState('')
  const [fingerprint, setFingerprint] = useState(existing?.sshHostFingerprint ?? '')
  const [heavySlots, setHeavySlots] = useState(slotsText(existing?.heavySlots))
  const [lightSlots, setLightSlots] = useState(slotsText(existing?.lightSlots))
  const [enabled, setEnabled] = useState(existing?.enabled ?? true)
  const [draining, setDraining] = useState(existing?.draining ?? false)
  const [invalid, setInvalid] = useState<string | null>(null)
  const [adviceApplied, setAdviceApplied] = useState(false)

  const stored = existing?.secrets
  const recordedFingerprint = existing?.sshHostFingerprint ?? null

  /** The create body, or the PATCH body with only what changed (and fits the host's kind). */
  function buildBody(): CreateSandboxHostBody | UpdateSandboxHostBody {
    const common = {
      name: name.trim(),
      heavySlots: slotsValue(heavySlots),
      lightSlots: slotsValue(lightSlots),
      enabled,
      draining,
    }
    const portValue = port.trim() === '' ? undefined : Number(port.trim())
    const tlsSecrets = Object.fromEntries(
      TLS_FIELDS.filter(({ key }) => tls[key].trim() !== '').map(({ key }) => [key, tls[key].trim()])
    ) as Partial<Record<TlsSecret, string>>

    if (!existing) {
      return kind === 'tcp'
        ? { kind, ...common, host: host.trim(), port: portValue, tlsEnabled, ...(tlsEnabled ? tlsSecrets : {}) }
        : {
            kind,
            ...common,
            host: host.trim(),
            port: portValue,
            sshUser: sshUser.trim(),
            sshPrivateKey: sshPrivateKey.trim(),
            ...(fingerprint.trim() ? { sshHostFingerprint: fingerprint.trim() } : {}),
          }
    }

    const candidate: UpdateSandboxHostBody = {
      ...common,
      host: host.trim(),
      ...(portValue !== undefined ? { port: portValue } : {}),
      tlsEnabled,
      ...(tlsEnabled ? tlsSecrets : {}),
      sshUser: sshUser.trim(),
      ...(sshPrivateKey.trim() ? { sshPrivateKey: sshPrivateKey.trim() } : {}),
      // Emptied → forget the recorded fingerprint; the worker pins the next one.
      sshHostFingerprint: fingerprint.trim() === '' ? null : fingerprint.trim(),
    }
    const body: Record<string, unknown> = {}
    for (const field of SANDBOX_HOST_EDITABLE_FIELDS[existing.kind]) {
      const value = candidate[field]
      if (value === undefined) continue
      // Secrets have no stored value to compare with: a typed one is always new.
      const current = (existing as unknown as Record<string, unknown>)[field]
      if (current !== undefined && current === value) continue
      body[field] = value
    }
    return body as UpdateSandboxHostBody
  }

  const save = useMutation({
    mutationFn: (body: CreateSandboxHostBody | UpdateSandboxHostBody) =>
      existing
        ? sandboxHostsApi.update(existing.id, body as UpdateSandboxHostBody)
        : sandboxHostsApi.create(body as CreateSandboxHostBody),
    onSuccess: onDone,
  })

  function handleSubmit(event: FormEvent): void {
    event.preventDefault()
    const body = buildBody()
    const parsed = existing
      ? UpdateSandboxHostBodySchema.safeParse(body)
      : CreateSandboxHostBodySchema.safeParse(body)
    if (!parsed.success) {
      setInvalid(parsed.error.issues[0]?.message ?? 'Check the fields.')
      return
    }
    if (tlsMissing) {
      setInvalid('TLS needs the CA, client certificate and client key')
      return
    }
    setInvalid(null)
    save.mutate(body)
  }

  const showTcp = !isLocal && !isTunnel && kind === 'tcp'
  const showSsh = !isLocal && !isTunnel && kind === 'ssh'
  // With TLS on, every secret must be typed now or already stored.
  const tlsMissing =
    showTcp && tlsEnabled && TLS_FIELDS.some(({ key }) => tls[key].trim() === '' && !stored?.[key])
  const canSave =
    name.trim() !== '' &&
    (isLocal ||
      isTunnel ||
      (host.trim() !== '' &&
        (showTcp ? !tlsMissing : sshUser.trim() !== '' && (sshPrivateKey.trim() !== '' || stored?.sshPrivateKey === true))))

  const portPlaceholder = String(defaultSandboxHostPort(kind, tlsEnabled))

  return (
    <form className="space-y-5" onSubmit={handleSubmit}>
      {!existing ? (
        <fieldset>
          <legend className="mb-2 text-base font-medium text-ink">How is Docker reached?</legend>
          <div className="space-y-2">
            {KINDS.map((option) => {
              const selected = option.value === kind
              return (
                <label
                  key={option.value}
                  className={`flex cursor-pointer gap-3 rounded-control border px-4 py-2.5 transition-colors ${
                    selected ? 'border-action bg-action/5' : 'border-line hover:border-line-strong'
                  }`}
                >
                  <input
                    type="radio"
                    name="sandbox-host-kind"
                    className="mt-1"
                    checked={selected}
                    onChange={() => setKind(option.value)}
                  />
                  <span>
                    <span className="block text-base font-medium text-ink">{option.label}</span>
                    <span className="block text-sm text-ink-3">{option.text}</span>
                  </span>
                </label>
              )
            })}
          </div>
        </fieldset>
      ) : null}

      <Field label="Name" htmlFor="sandbox-host-name" hint="Shown on runs that started here.">
        <input
          id="sandbox-host-name"
          className="field-input"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Build server 1"
          maxLength={80}
          required
        />
      </Field>

      {!isLocal && !isTunnel ? (
        <div className="grid gap-4 sm:grid-cols-[1fr_8rem]">
          <Field label="Host" htmlFor="sandbox-host-host" hint="A host name or IP address, without scheme or port; a tailnet name works.">
            <input
              id="sandbox-host-host"
              className="field-input font-mono"
              value={host}
              onChange={(event) => setHost(event.target.value)}
              placeholder="build-1.tailnet.ts.net"
              autoCapitalize="none"
              spellCheck={false}
              required
            />
          </Field>
          <Field label="Port" htmlFor="sandbox-host-port">
            <input
              id="sandbox-host-port"
              className="field-input font-mono"
              inputMode="numeric"
              value={port}
              onChange={(event) => setPort(event.target.value)}
              placeholder={portPlaceholder}
            />
          </Field>
        </div>
      ) : null}

      {showTcp ? (
        <>
          <label className="flex items-center gap-2.5 text-base text-ink">
            <input
              type="checkbox"
              checked={tlsEnabled}
              onChange={(event) => setTlsEnabled(event.target.checked)}
              className="h-4 w-4 accent-[rgb(var(--moss))]"
            />
            Use TLS
          </label>
          {tlsEnabled ? (
            TLS_FIELDS.map(({ key, label, placeholder }) => (
              <SecretField
                key={key}
                id={`sandbox-host-${key}`}
                label={label}
                stored={stored?.[key] === true}
                value={tls[key]}
                onChange={(value) => setTls((current) => ({ ...current, [key]: value }))}
                placeholder={placeholder}
              />
            ))
          ) : (
            <Notice tone="signal">
              Only inside a private network such as Tailscale or WireGuard. Without TLS anyone
              who reaches the port controls Docker on that machine.
            </Notice>
          )}
        </>
      ) : null}

      {showSsh ? (
        <>
          <Field label="User" htmlFor="sandbox-host-ssh-user" hint="A user in the docker group on that machine.">
            <input
              id="sandbox-host-ssh-user"
              className="field-input font-mono"
              value={sshUser}
              onChange={(event) => setSshUser(event.target.value)}
              placeholder="goblic"
              autoCapitalize="none"
              spellCheck={false}
              required
            />
          </Field>
          <SecretField
            id="sandbox-host-ssh-key"
            label="Private key"
            stored={stored?.sshPrivateKey === true}
            value={sshPrivateKey}
            onChange={setSshPrivateKey}
            placeholder="-----BEGIN OPENSSH PRIVATE KEY-----"
          />
          <Field
            label="Host fingerprint"
            htmlFor="sandbox-host-fingerprint"
            hint={
              <>
                Optional. Get it with{' '}
                <code className="font-mono">ssh-keyscan -p {port.trim() || '22'} {host.trim() || 'host'} | ssh-keygen -lf -</code>
                . Empty: the first connection’s key is recorded and required from then on.
              </>
            }
          >
            <div className="flex gap-2">
              <input
                id="sandbox-host-fingerprint"
                className="field-input font-mono"
                value={fingerprint}
                onChange={(event) => setFingerprint(event.target.value)}
                placeholder="SHA256:…"
                autoCapitalize="none"
                spellCheck={false}
              />
              {recordedFingerprint && fingerprint !== '' ? (
                <button
                  type="button"
                  className="btn-secondary shrink-0"
                  onClick={() => setFingerprint('')}
                  title="Forget it; the next connection’s key is recorded instead"
                >
                  Forget
                </button>
              ) : null}
            </div>
          </Field>
          {recordedFingerprint && fingerprint === '' ? (
            <Notice tone="signal">
              The recorded fingerprint is forgotten when you save; the next connection’s key is
              trusted. Only do this when the host’s SSH key really changed.
            </Notice>
          ) : null}
        </>
      ) : null}

      <fieldset>
        <legend className="mb-1 text-base font-medium text-ink">Capacity</legend>
        <p className="mb-2 text-sm text-ink-3">
          Sandboxes at the same time. Empty = no limit here; only the workers’ own slots count.
          Hosts with a limit are used first.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Heavy runs" htmlFor="sandbox-host-heavy" hint="Agents that build and test code.">
            <input
              id="sandbox-host-heavy"
              type="number"
              min={0}
              max={1000}
              className="field-input"
              value={heavySlots}
              onChange={(event) => setHeavySlots(event.target.value)}
              placeholder="No limit"
            />
          </Field>
          <Field label="Light runs" htmlFor="sandbox-host-light" hint="Planning, reviews and other light work.">
            <input
              id="sandbox-host-light"
              type="number"
              min={0}
              max={1000}
              className="field-input"
              value={lightSlots}
              onChange={(event) => setLightSlots(event.target.value)}
              placeholder="No limit"
            />
          </Field>
        </div>
        {adviceApplied ? <p className="mt-2 text-sm text-ink-3">Applied — save to keep it.</p> : null}
      </fieldset>

      {existing ? (
        <SandboxHostScanPanel
          hostId={existing.id}
          initial={existing}
          autoStart={autoScan}
          slots={{ heavySlots: slotsValue(heavySlots), lightSlots: slotsValue(lightSlots) }}
          onApply={(advice) => {
            setHeavySlots(String(advice.heavySlots))
            setLightSlots(String(advice.lightSlots))
            setAdviceApplied(true)
          }}
        />
      ) : null}

      <div className="space-y-2">
        <label className="flex items-center gap-2.5 text-base text-ink">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(event) => setEnabled(event.target.checked)}
            className="h-4 w-4 accent-[rgb(var(--moss))]"
          />
          Enabled
        </label>
        <label className="flex items-center gap-2.5 text-base text-ink">
          <input
            type="checkbox"
            checked={draining}
            onChange={(event) => setDraining(event.target.checked)}
            className="h-4 w-4 accent-[rgb(var(--moss))]"
          />
          Draining: start no new sandboxes here, let running ones finish
        </label>
      </div>

      {invalid ? <Notice tone="error">{invalid}</Notice> : null}
      {save.isError ? (
        <Notice tone="error">{errorText(save.error, 'Couldn’t save the sandbox host.')}</Notice>
      ) : null}

      <div className="flex justify-end border-t border-line pt-4">
        <button type="submit" className="btn-primary" disabled={save.isPending || !canSave}>
          {save.isPending ? 'Saving…' : existing ? 'Save' : 'Add host'}
        </button>
      </div>
    </form>
  )
}

/** A PEM pasted once; when one is stored the field only replaces it. */
function SecretField({
  id,
  label,
  stored,
  value,
  onChange,
  placeholder,
}: {
  id: string
  label: string
  stored: boolean
  value: string
  onChange: (value: string) => void
  placeholder: string
}): ReactNode {
  return (
    <Field
      label={stored ? `Replace ${label.replace(/^[A-Z][a-z]/, (start) => start.toLowerCase())}` : label}
      htmlFor={id}
      hint={stored ? KEEP_HINT : 'PEM text. Stored encrypted and never shown again.'}
    >
      <textarea
        id={id}
        className="field-input font-mono"
        rows={4}
        autoComplete="off"
        spellCheck={false}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
      />
    </Field>
  )
}

/**
 * Step 1 of "Add host with installer" (installer ontwerp §1): a name and an optional
 * capacity. Nothing about the machine is asked; the install command does the rest.
 */
export function TunnelHostForm({
  onDone,
}: {
  onDone: (created: CreateTunnelSandboxHostResponse) => void
}) {
  const [name, setName] = useState('')
  const [heavySlots, setHeavySlots] = useState('')
  const [lightSlots, setLightSlots] = useState('')
  const [enabled, setEnabled] = useState(true)
  const [invalid, setInvalid] = useState<string | null>(null)

  const create = useMutation({
    mutationFn: (body: CreateSandboxHostBody) => sandboxHostsApi.createTunnel(body),
    onSuccess: onDone,
  })

  function handleSubmit(event: FormEvent): void {
    event.preventDefault()
    const body = {
      kind: 'tunnel' as const,
      name: name.trim(),
      heavySlots: slotsValue(heavySlots),
      lightSlots: slotsValue(lightSlots),
      enabled,
    }
    const parsed = CreateSandboxHostBodySchema.safeParse(body)
    if (!parsed.success) {
      setInvalid(parsed.error.issues[0]?.message ?? 'Check the fields.')
      return
    }
    setInvalid(null)
    create.mutate(body)
  }

  return (
    <form className="space-y-5" onSubmit={handleSubmit}>
      <Field label="Name" htmlFor="tunnel-host-name" hint="Shown on runs that started here.">
        <input
          id="tunnel-host-name"
          className="field-input"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Build server 1"
          maxLength={80}
          required
        />
      </Field>
      <fieldset>
        <legend className="mb-1 text-base font-medium text-ink">Capacity</legend>
        <p className="mb-2 text-sm text-ink-3">
          Sandboxes at the same time. Empty = no limit here. You can change this later.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Heavy runs" htmlFor="tunnel-host-heavy">
            <input
              id="tunnel-host-heavy"
              type="number"
              min={0}
              max={1000}
              className="field-input"
              value={heavySlots}
              onChange={(event) => setHeavySlots(event.target.value)}
              placeholder="No limit"
            />
          </Field>
          <Field label="Light runs" htmlFor="tunnel-host-light">
            <input
              id="tunnel-host-light"
              type="number"
              min={0}
              max={1000}
              className="field-input"
              value={lightSlots}
              onChange={(event) => setLightSlots(event.target.value)}
              placeholder="No limit"
            />
          </Field>
        </div>
      </fieldset>
      <label className="flex items-center gap-2.5 text-base text-ink">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(event) => setEnabled(event.target.checked)}
          className="h-4 w-4 accent-[rgb(var(--moss))]"
        />
        Enabled
      </label>
      {invalid ? <Notice tone="error">{invalid}</Notice> : null}
      {create.isError ? (
        <Notice tone="error">{errorText(create.error, 'Couldn’t create the sandbox host.')}</Notice>
      ) : null}
      <div className="flex justify-end border-t border-line pt-4">
        <button type="submit" className="btn-primary" disabled={create.isPending || name.trim() === ''}>
          {create.isPending ? 'Creating…' : 'Create'}
        </button>
      </div>
    </form>
  )
}
